import React, { FormEvent, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Mail,
  RotateCcw,
} from 'lucide-react'
import Seo from '../components/Seo'

type StoryKey = 'A' | 'B' | 'C' | 'D' | 'E' | 'F'

interface AnswerOption {
  key: StoryKey
  text: string
}

interface Question {
  id: number
  title: string
  options: AnswerOption[]
}

interface StoryResult {
  key: StoryKey
  title: string
  description: string
  nextStep: string
}

const questions: Question[] = [
  {
    id: 1,
    title: 'Getting Started',
    options: [
      {
        key: 'A',
        text: 'Exercise language, gym equipment, and where to even begin all feel unfamiliar to me.',
      },
      {
        key: 'B',
        text: 'I know exactly what to do — I just never have anything left to actually do it.',
      },
      {
        key: 'C',
        text: "Starting isn't the hard part. Staying with it is.",
      },
      {
        key: 'D',
        text: 'I want to start, but an old injury or physical limitation makes me cautious.',
      },
      {
        key: 'E',
        text: "I used to know what worked. Now my body responds differently, and I'm not sure what to do instead.",
      },
      {
        key: 'F',
        text: "I'm not starting from nothing — I'm ready to push toward something bigger than I've done before.",
      },
    ],
  },
  {
    id: 2,
    title: 'Energy & Rest',
    options: [
      {
        key: 'A',
        text: "I don't really have a rhythm yet with rest or recovery — I'm still learning what my body needs.",
      },
      {
        key: 'B',
        text: 'Rest feels like something I have to earn, so I rarely actually take it.',
      },
      {
        key: 'C',
        text: "Some weeks I recover well. Other weeks it falls apart completely, and I don't know why.",
      },
      {
        key: 'D',
        text: "I'm learning how to balance movement and recovery around a physical limitation.",
      },
      {
        key: 'E',
        text: "My energy and recovery don't work the way they used to, and it's thrown off everything I thought I knew.",
      },
      {
        key: 'F',
        text: "I know how to push hard — the part I'm working on is knowing when to actually back off.",
      },
    ],
  },
  {
    id: 3,
    title: 'Consistency',
    options: [
      {
        key: 'A',
        text: 'I haven’t built a routine long enough yet to know what “consistent” even looks like for me.',
      },
      {
        key: 'B',
        text: "I'm consistent with everyone else's needs. My own routine is the first thing to go.",
      },
      {
        key: 'C',
        text: 'This is my exact struggle — I can start, but staying with it long-term is where I lose momentum.',
      },
      {
        key: 'D',
        text: "I stay consistent with things that don't aggravate what's already going on in my body — everything else is trial and error.",
      },
      {
        key: 'E',
        text: "What used to keep me consistent doesn't work the same way for my body anymore.",
      },
      {
        key: 'F',
        text: "I'm consistent — I'm looking for a plan precise enough to match the level I'm training at.",
      },
    ],
  },
  {
    id: 4,
    title: 'Your Body Right Now',
    options: [
      {
        key: 'A',
        text: "I don't have a strong sense yet of what my body can or can't do — I haven't tested it much.",
      },
      {
        key: 'B',
        text: "My schedule makes it hard to give my own health and fitness the attention I'd like.",
      },
      {
        key: 'C',
        text: "My body's capable. I just haven't found a way of training that I can sustain.",
      },
      {
        key: 'D',
        text: "There's a specific injury, joint, or limitation I have to train around, and I need a plan that respects it.",
      },
      {
        key: 'E',
        text: "My body has genuinely changed — and I'm trying to figure out what it's actually capable of now.",
      },
      {
        key: 'F',
        text: "I have a solid foundation and want to see what I can accomplish with a more focused plan.",
      },
    ],
  },
  {
    id: 5,
    title: "What You're Looking For",
    options: [
      {
        key: 'A',
        text: 'Someone to teach me the fundamentals without making me feel behind for not already knowing them.',
      },
      {
        key: 'B',
        text: "A plan that fits inside a life that's already full — not one more thing demanding space I don't have.",
      },
      {
        key: 'C',
        text: 'A system built around my actual life, not more willpower or more information.',
      },
      {
        key: 'D',
        text: 'A coach who will build around my limitation instead of ignoring it or telling me to push through it.',
      },
      {
        key: 'E',
        text: 'Someone who understands that my body operates differently now than it did ten years ago.',
      },
      {
        key: 'F',
        text: 'A coach who can match my ambition without asking me to sacrifice my health or sanity to get there.',
      },
    ],
  },
  {
    id: 6,
    title: 'Your Honest Goal',
    options: [
      {
        key: 'A',
        text: 'To finally understand how to train and eat well, without pretending I already know.',
      },
      {
        key: 'B',
        text: 'To feel like myself again — energized, not running on empty.',
      },
      {
        key: 'C',
        text: 'To build something that actually sticks, instead of restarting every few months.',
      },
      {
        key: 'D',
        text: 'To move and train confidently again, without fear of making an old issue worse.',
      },
      {
        key: 'E',
        text: 'To feel strong and capable in the body I have now — not the one I used to have.',
      },
      {
        key: 'F',
        text: 'To reach a genuinely ambitious goal — on stage or beyond — the right way.',
      },
    ],
  },
]

const storyResults: Record<StoryKey, StoryResult> = {
  A: {
    key: 'A',
    title: 'The First-Time Starter',
    description:
      "You haven't been here before, and that's not a problem to fix before you start — it's just where you're starting from. You don't need to know the difference between a squat and a Romanian deadlift, and you don't need to already understand macros. What you need is a coach willing to teach the fundamentals without making not-knowing feel like falling behind.",
    nextStep:
      "Give yourself permission to ask beginner questions without apologizing for them. The women who build real strength aren't the ones who start out knowing everything — they're the ones who let themselves learn.",
  },
  B: {
    key: 'B',
    title: 'The Burnt-Out Professional',
    description:
      "You're not lacking discipline. You're lacking bandwidth — because everyone and everything else has been getting your best energy for a long time. The plan you need isn't more intense. It's one that treats your rest and your recovery as part of the work, not a reward you have to earn after you've done enough.",
    nextStep:
      "This week, protect one thing — one workout, one earlier bedtime, one meal you actually plan for — the same way you'd protect a client meeting. Treat it as non-negotiable, not optional.",
  },
  C: {
    key: 'C',
    title: 'The Sustainable Habit-Builder',
    description:
      "Starting was never your problem. The plan falling apart three weeks in — that's the actual pattern you're trying to break. What you need isn't more information or more willpower. You need a system built around your real week, not an idealized one.",
    nextStep:
      "Instead of planning the perfect week, plan the realistic one. Pick the one habit you're most likely to keep even on your worst week, and build from there — not from your best week.",
  },
  D: {
    key: 'D',
    title: 'The Structural Turnaround',
    description:
      "You're not fragile, and you're not out of options — you have a real physical limitation that deserves a plan built around it, not a generic program that ignores it or asks you to push through it. Progress doesn't have to mean pain.",
    nextStep:
      "Write down, plainly, what your body needs you to avoid right now — the specific movement, position, or intensity that flares things up. That list isn't a limitation on your training. It's the starting information any good coach needs. If you haven't already, a doctor or physical therapist is the right person to confirm what's safe for your specific injury or limitation — coaching builds around that guidance, it doesn't replace it.",
  },
  E: {
    key: 'E',
    title: 'The Midlife Rebuild',
    description:
      "Your body isn't broken. It's different — and different comes with its own rules, not a dead end. The plan that used to work doesn't have to work the same way again for you to keep making progress. It just has to be built for who you are now, not who you were ten years ago.",
    nextStep:
      'Notice one thing your body is asking for differently than it used to — more recovery, different fuel, a different pace — and let yourself honor it instead of fighting it this week.',
  },
  F: {
    key: 'F',
    title: 'The Competitor',
    description:
      "You already have the drive. What you're looking for isn't motivation — it's a coach who can match your ambition without asking you to trade your health or your sanity to get there. Hard doesn't have to mean reckless.",
    nextStep:
      'Get specific about the actual goal — a show, a physique milestone, a personal best — and write down what “getting there without wrecking myself” would actually require. That’s the conversation worth having with a coach.',
  },
}

const storyOrder: StoryKey[] = ['A', 'B', 'C', 'D', 'E', 'F']

const StartingOverGuide: React.FC = () => {
  const [answers, setAnswers] = useState<Record<number, StoryKey>>({})
  const [results, setResults] = useState<StoryResult[] | null>(null)
  const [expandedKeys, setExpandedKeys] = useState<Set<StoryKey>>(new Set())

  const [firstName, setFirstName] = useState('')
  const [email, setEmail] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [deliverySuccess, setDeliverySuccess] = useState(false)
  const [deliveryError, setDeliveryError] = useState('')

  const answeredCount = Object.keys(answers).length
  const isComplete = answeredCount === questions.length

  const progress = useMemo(
    () => Math.round((answeredCount / questions.length) * 100),
    [answeredCount]
  )

  const chooseAnswer = (questionId: number, key: StoryKey) => {
    setAnswers((current) => ({
      ...current,
      [questionId]: key,
    }))

    if (results) {
      setResults(null)
    }

    setDeliverySuccess(false)
    setDeliveryError('')
  }

  const calculateResult = () => {
    if (!isComplete) return

    const totals: Record<StoryKey, number> = {
      A: 0,
      B: 0,
      C: 0,
      D: 0,
      E: 0,
      F: 0,
    }

    Object.values(answers).forEach((answer) => {
      totals[answer] += 1
    })

    const maxCount = Math.max(...storyOrder.map((key) => totals[key]))
    const winningKeys = storyOrder.filter((key) => totals[key] === maxCount)
    const winningResults = winningKeys.map((key) => storyResults[key])

    setResults(winningResults)
    // Multiple tied results start collapsed except the first, so the page
    // doesn't open with every description expanded at once.
    setExpandedKeys(new Set(winningKeys.length > 1 ? [winningKeys[0]] : winningKeys))

    window.setTimeout(() => {
      document
        .getElementById('assessment-result')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }

  const toggleExpanded = (key: StoryKey) => {
    setExpandedKeys((current) => {
      const next = new Set(current)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const resetAssessment = () => {
    setAnswers({})
    setResults(null)
    setExpandedKeys(new Set())
    setDeliverySuccess(false)
    setDeliveryError('')

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  const sendGuide = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!firstName.trim() || !email.trim()) {
      setDeliveryError('Please enter your first name and email address.')
      return
    }

    setIsSending(true)
    setDeliveryError('')
    setDeliverySuccess(false)

    try {
      const response = await fetch('/api/starting-over-guide', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: firstName.trim(),
          email: email.trim(),
          result: results ? results.map((r) => r.key).join(',') : '',
        }),
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok || !data.ok || !data.delivered) {
        throw new Error(
          data.error ||
            'We could not send your guide right now. Please try again.'
        )
      }

      setDeliverySuccess(true)
    } catch (error) {
      setDeliveryError(
        error instanceof Error
          ? error.message
          : 'We could not send your guide right now. Please try again.'
      )
    } finally {
      setIsSending(false)
    }
  }

  return (
    <main className="min-h-screen bg-brand-cream text-brand-navy">
      <Seo
        title="Which Starting-Over Story Is Yours? | Fit by Cassie"
        description="Find your starting point with the free Fit by Cassie Starting-Over assessment and guide."
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-navy text-brand-cream">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            background:
              'radial-gradient(circle at top right, rgba(100,143,139,.85), transparent 38%), radial-gradient(circle at bottom left, rgba(215,170,165,.45), transparent 34%)',
          }}
          aria-hidden="true"
        />

        <div className="relative container-max px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-28">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="max-w-4xl"
          >
            <span className="inline-flex rounded-full border border-brand-cream/20 bg-brand-cream/10 px-4 py-2 text-xs font-bold uppercase tracking-widest-xl text-brand-cream/90">
              Free Starting-Over Assessment
            </span>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] mt-6">
              Which Starting-Over
              <span className="block italic text-brand-blush">
                Story Is Yours?
              </span>
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-brand-cream/80 leading-relaxed max-w-2xl">
              A guide to finding your starting point — and what to do next.
            </p>

            <p className="mt-4 text-sm sm:text-base text-brand-cream/65 max-w-2xl leading-relaxed">
              Your starting point is information. It isn't a qualification
              test.
            </p>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-brand-ivory border-b border-brand-navy/10">
        <div className="container-max px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-3xl">
            <p className="text-brand-navy/75 leading-relaxed">
              These six starting points aren't personality types or permanent
              labels. They're different reasons you might be ready for a
              change right now, and your starting point can shift as life
              changes.
            </p>

            <p className="mt-4 text-brand-navy/75 leading-relaxed">
              Choose the answer that feels most true for you today. There are
              no right or wrong answers. Once you've answered all six
              questions, I'll help you understand what your answers might
              mean for your next step.
            </p>
          </div>
        </div>
      </section>

      {/* ASSESSMENT */}
      <section className="py-16 sm:py-20">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-3xl bg-brand-ivory border border-brand-navy/10 shadow-soft p-6 sm:p-8 mb-10">
              <div className="flex items-center justify-between gap-4 mb-4">
                <span className="text-xs font-bold uppercase tracking-widest-xl text-brand-teal">
                  Your Progress
                </span>

                <span className="text-sm font-semibold text-brand-navy/65">
                  {answeredCount} of {questions.length} answered
                </span>
              </div>

              <div className="h-2 rounded-full bg-brand-navy/10 overflow-hidden">
                <motion.div
                  className="h-full bg-brand-teal rounded-full"
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>

            <div className="space-y-8">
              {questions.map((question) => (
                <div
                  key={question.id}
                  className="rounded-3xl bg-brand-ivory border border-brand-navy/10 shadow-soft p-6 sm:p-8"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-navy text-brand-cream font-bold">
                      {question.id}
                    </div>

                    <h2 className="font-display text-2xl sm:text-3xl font-semibold leading-tight pt-1">
                      {question.title}
                    </h2>
                  </div>

                  <div className="space-y-3">
                    {question.options.map((option) => {
                      const selected = answers[question.id] === option.key

                      return (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() =>
                            chooseAnswer(question.id, option.key)
                          }
                          className={`w-full text-left rounded-2xl border p-4 sm:p-5 transition-all duration-200 ${
                            selected
                              ? 'border-brand-teal bg-brand-teal/10 shadow-soft'
                              : 'border-brand-navy/10 bg-white hover:border-brand-teal/50 hover:bg-brand-teal/5'
                          }`}
                        >
                          <div className="flex gap-4">
                            <span
                              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                                selected
                                  ? 'bg-brand-teal text-white'
                                  : 'bg-brand-cream text-brand-navy'
                              }`}
                            >
                              {selected ? <Check size={16} /> : option.key}
                            </span>

                            <span className="text-brand-navy/80 leading-relaxed pt-1">
                              {option.text}
                            </span>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-10">
              <button
                type="button"
                disabled={!isComplete}
                onClick={calculateResult}
                className={`inline-flex items-center justify-center rounded-full font-bold px-8 py-4 transition-all duration-300 ${
                  isComplete
                    ? 'bg-brand-navy text-brand-cream hover:-translate-y-0.5 hover:shadow-soft-lg'
                    : 'bg-brand-navy/20 text-brand-navy/40 cursor-not-allowed'
                }`}
              >
                Find My Starting Point
                <ArrowRight className="ml-2" size={19} />
              </button>

              {!isComplete && (
                <p className="mt-3 text-sm text-brand-navy/55">
                  Answer all six questions to see your result.
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* RESULT */}
      {results && results.length > 0 && (
        <section
          id="assessment-result"
          className="scroll-mt-28 bg-brand-navy text-brand-cream py-16 sm:py-20"
        >
          <div className="container-max px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto"
            >
              <span className="text-xs font-bold uppercase tracking-widest-xl text-brand-blush">
                {results.length === 1
                  ? `Your Starting Point — Letter ${results[0].key}`
                  : `Your Starting Points — Letters ${results
                      .map((r) => r.key)
                      .join(', ')}`}
              </span>

              {results.length === 1 ? (
                <>
                  <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-5">
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-brand-cream text-brand-navy font-display text-3xl font-bold">
                      {results[0].key}
                    </div>

                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold">
                      {results[0].title}
                    </h2>
                  </div>

                  <p className="mt-7 text-lg text-brand-cream/80 leading-relaxed max-w-3xl">
                    {results[0].description}
                  </p>

                  <div className="mt-8 rounded-3xl border border-brand-cream/15 bg-brand-cream/10 p-6 sm:p-8">
                    <span className="text-xs font-bold uppercase tracking-widest-xl text-brand-blush">
                      Your Next Step
                    </span>

                    <p className="mt-4 text-brand-cream/85 leading-relaxed">
                      {results[0].nextStep}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold mt-5 leading-tight">
                    You may recognize yourself in more than one starting
                    point.
                  </h2>

                  <p className="mt-6 text-lg text-brand-cream/80 leading-relaxed max-w-3xl">
                    Your answers point to a combination of experiences.
                    That's completely normal. Your starting point isn't a
                    label, and you don't have to fit into one category to
                    move forward.
                  </p>

                  <div className="mt-8 space-y-4">
                    {results.map((r) => {
                      const isOpen = expandedKeys.has(r.key)

                      return (
                        <div
                          key={r.key}
                          className="rounded-3xl border border-brand-cream/15 bg-brand-cream/10 overflow-hidden"
                        >
                          <button
                            type="button"
                            onClick={() => toggleExpanded(r.key)}
                            aria-expanded={isOpen}
                            className="w-full flex items-center gap-4 p-5 sm:p-6 text-left"
                          >
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-cream text-brand-navy font-display text-lg font-bold">
                              {r.key}
                            </span>

                            <span className="font-display text-xl sm:text-2xl font-semibold flex-1">
                              {r.title}
                            </span>

                            <span
                              className="text-brand-cream/60 text-sm font-semibold shrink-0"
                              aria-hidden
                            >
                              {isOpen ? 'Hide' : 'Show'}
                            </span>
                          </button>

                          {isOpen && (
                            <div className="px-5 sm:px-6 pb-6">
                              <p className="text-brand-cream/80 leading-relaxed">
                                {r.description}
                              </p>

                              <div className="mt-5 rounded-2xl border border-brand-cream/15 bg-brand-navy/40 p-5">
                                <span className="text-xs font-bold uppercase tracking-widest-xl text-brand-blush">
                                  Your Next Step
                                </span>

                                <p className="mt-3 text-brand-cream/85 leading-relaxed">
                                  {r.nextStep}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </>
              )}

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/book-consultation"
                  className="inline-flex items-center justify-center rounded-full bg-brand-cream text-brand-navy font-bold px-7 py-3.5 transition-all duration-300 hover:-translate-y-0.5"
                >
                  Schedule Your Complimentary Consultation
                  <ArrowRight className="ml-2" size={18} />
                </Link>

                <button
                  type="button"
                  onClick={resetAssessment}
                  className="inline-flex items-center justify-center rounded-full border border-brand-cream/25 text-brand-cream font-semibold px-7 py-3.5 hover:bg-brand-cream/10 transition-colors"
                >
                  <RotateCcw className="mr-2" size={17} />
                  Retake Assessment
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* FREE GUIDE DELIVERY */}
      <section className="bg-brand-ivory py-16 sm:py-20">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto rounded-[2rem] border border-brand-navy/10 bg-white shadow-soft-lg overflow-hidden">
            <div className="grid lg:grid-cols-[1.05fr_.95fr]">
              <div className="p-7 sm:p-10 lg:p-12">
                <span className="text-xs font-bold uppercase tracking-widest-xl text-brand-teal">
                  Free Guide
                </span>

                <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-4">
                  Keep your Starting-Over guide handy.
                </h2>

                <p className="mt-5 text-brand-navy/70 leading-relaxed">
                  Want a copy you can revisit? I'll send you the six-question
                  assessment, all six starting-point descriptions, and the
                  closing invitation in one downloadable PDF.
                </p>

                <div className="mt-7 space-y-3 text-sm text-brand-navy/70">
                  {[
                    'The complete six-question assessment',
                    'All six Starting-Over results',
                    'A practical next step for every starting point',
                    'The closing consultation invitation',
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <CheckCircle2
                        size={18}
                        className="text-brand-teal shrink-0 mt-0.5"
                      />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-brand-cream p-7 sm:p-10 lg:p-12 flex items-center">
                <div className="w-full">
                  {deliverySuccess ? (
                    <div>
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-teal text-white mb-5">
                        <Check size={22} />
                      </div>

                      <h3 className="font-display text-2xl sm:text-3xl font-semibold">
                        Your guide is on its way.
                      </h3>

                      <p className="mt-4 text-brand-navy/70 leading-relaxed">
                        Check your inbox for your copy of{' '}
                        <em>Which Starting-Over Story Is Yours?</em>
                      </p>

                      <p className="mt-3 text-sm text-brand-navy/55 leading-relaxed">
                        If you don't see it shortly, check your spam or
                        promotions folder.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={sendGuide}>
                      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-navy text-brand-cream mb-5">
                        <Mail size={20} />
                      </div>

                      <h3 className="font-display text-2xl sm:text-3xl font-semibold">
                        Send me the free guide.
                      </h3>

                      <p className="mt-3 text-sm text-brand-navy/60 leading-relaxed">
                        Enter your first name and email, and I'll send the
                        free guide straight to your inbox.
                      </p>

                      <div className="mt-7 space-y-4">
                        <div>
                          <label
                            htmlFor="guide-first-name"
                            className="block text-sm font-semibold text-brand-navy mb-2"
                          >
                            First Name
                          </label>

                          <input
                            id="guide-first-name"
                            type="text"
                            value={firstName}
                            onChange={(event) =>
                              setFirstName(event.target.value)
                            }
                            autoComplete="given-name"
                            required
                            maxLength={80}
                            className="w-full rounded-xl border-brand-navy/15 bg-white text-brand-navy focus:border-brand-teal focus:ring-brand-teal"
                          />
                        </div>

                        <div>
                          <label
                            htmlFor="guide-email"
                            className="block text-sm font-semibold text-brand-navy mb-2"
                          >
                            Email
                          </label>

                          <input
                            id="guide-email"
                            type="email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            autoComplete="email"
                            required
                            maxLength={200}
                            className="w-full rounded-xl border-brand-navy/15 bg-white text-brand-navy focus:border-brand-teal focus:ring-brand-teal"
                          />
                        </div>
                      </div>

                      {results && results.length > 0 && (
                        <p className="mt-4 text-xs text-brand-navy/55">
                          Your assessment result:{' '}
                          <span className="font-semibold">
                            {results.map((r) => r.title).join(' + ')}
                          </span>
                        </p>
                      )}

                      {deliveryError && (
                        <div
                          role="alert"
                          className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                        >
                          {deliveryError}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={isSending}
                        className="mt-6 w-full inline-flex items-center justify-center rounded-full bg-brand-navy text-brand-cream font-bold px-7 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-soft-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                      >
                        {isSending ? 'Sending Your Guide...' : 'Send Me the Free Guide'}
                        {!isSending && (
                          <ArrowRight className="ml-2" size={18} />
                        )}
                      </button>

                      <p className="mt-4 text-xs text-brand-navy/45 leading-relaxed text-center">
                        No spam. Just your guide and relevant Fit by Cassie
                        updates.
                      </p>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </main>
  )
}

export default StartingOverGuide
